import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { FormField } from '~/eds/src/components/FormField/FormField';
import { Label } from '~/eds/src/components/Label/Label';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Select } from '~/eds/src/components/Select/Select';
import { Text } from '~/eds/src/components/Text/Text';
import { TextInput } from '~/eds/src/components/TextInput/TextInput';
import { useFormatNumber } from '~/i18n/hooks/useFormatNumber/useFormatNumber';
import { slices } from '~/redux/slices';
import Alert from '~/ui/components/Alert/Alert';
import { htmlToText } from '~/utils/strings';

import { CKEditor } from '../../Shared/CKEditor4/CKEditor4';
import {
  CLAUSE_LIBRARY_NAME_CHAR_LIMIT,
  GUIDANCE_NOTES_CHAR_LIMIT,
  VARIATION_TEXT_CHAR_LIMIT,
} from '../constants';

const VariationForm = () => {
  const intl = useIntl();
  const formatNumber = useFormatNumber();
  const dispatch = useDispatch();
  const variationForm = useSelector(
    slices.clauseLibrary.selectors.selectVariationForm,
  );

  const editorConfig = {
    editorplaceholder: intl.formatMessage({
      id: 'VariationForm.clauseTextPlaceholder',
      defaultMessage: 'Clause Text or Guidance is required.',
      description: 'Placeholder text for the CKEditor when no text is entered',
    }),
    toolbar: [
      { name: 'editing', items: ['Undo', 'Redo'] },
      {
        name: 'text',
        items: ['Format', 'Bold', 'Italic', 'Underline', 'Strike'],
      },
      { name: 'link', items: ['Link', 'Unlink'] },
      {
        name: 'format',
        items: ['JustifyLeft', 'JustifyCenter', 'JustifyRight', 'JustifyBlock'],
      },
      {
        name: 'paragraph',
        items: ['BulletedList', 'NumberedList', 'BidiLtr', 'BidiRtl'],
      },
    ],
    toolbarGroups: [
      { name: 'group1', groups: ['editing'] },
      { name: 'group2', groups: ['text'] },
      { name: 'group3', groups: ['link'] },
      { name: 'group4', groups: ['format'] },
      { name: 'group5', groups: ['paragraph'] },
    ],
  };

  const positionOptions = [
    {
      label: intl.formatMessage({
        id: 'VariationForm.preferredOption',
        defaultMessage: 'Preferred',
        description: 'Preferred position option',
      }),
      value: 'Preferred',
    },
    {
      label: intl.formatMessage({
        id: 'VariationForm.fallbackOption',
        defaultMessage: 'Fallback',
        description: 'Fallback position option',
      }),
      value: 'Fallback',
    },
    {
      label: intl.formatMessage({
        id: 'VariationForm.walkAwayOption',
        defaultMessage: 'Walk away',
        description: 'Walk away position option',
      }),
      value: 'Walk away',
    },
  ];

  const clauseMissingText = intl.formatMessage({
    id: 'VariationForm.textOrGuidanceRequiredError',
    defaultMessage:
      'Clause Text or Guidance must be filled in. Both cannot be empty.',
    description: 'Error message when both text and guidance are empty',
  });

  const [variationName, setVariationName] = useState(variationForm.name || '');
  const [guidanceNotes, setGuidanceNotes] = useState(
    variationForm.guidanceNotes || '',
  );
  const [clauseText, setClauseText] = useState(variationForm.clauseText || '');
  const [position, setPosition] = useState(variationForm.position || undefined);

  const variationNameFooter = `${
    variationName.trim().length
  }/${CLAUSE_LIBRARY_NAME_CHAR_LIMIT}`;
  const variationNameOverLimit =
    variationName.trim().length > CLAUSE_LIBRARY_NAME_CHAR_LIMIT;
  const variationNameAPIErrors = variationForm.errors?.filter(
    (error) => error.source?.pointer === '/nickname',
  );
  const getVariationNameError = () => {
    if (variationNameOverLimit)
      return intl.formatMessage(
        {
          id: 'VariationForm.nameOverLimitError',
          defaultMessage:
            '{footer} (Name cannot be longer than {limit} characters)',
          description:
            'Error message when variation name exceeds character limit. Example: footer = "Name is too long", limit = "100"',
        },
        {
          footer: variationNameFooter,
          limit: CLAUSE_LIBRARY_NAME_CHAR_LIMIT,
        },
      );
    if (variationNameAPIErrors?.length)
      return variationNameAPIErrors.map((error) => error.detail).join(' ');
    return undefined;
  };

  // must come from validation errors because they shouldn't display until submission
  const positionMissing = variationForm.validationErrors?.find(
    (message) => message === 'position missing',
  );
  const positionAPIErrors = variationForm.errors?.filter(
    (error) => error.source?.pointer === '/position',
  );
  const getPositionError = () => {
    if (positionMissing)
      return intl.formatMessage({
        id: 'VariationForm.positionRequiredError',
        defaultMessage: 'This is a required field',
        description: 'Error message when position is required',
      });
    if (positionAPIErrors?.length)
      return positionAPIErrors.map((error) => error.detail).join(' ');
    return undefined;
  };

  const guidanceNotesText = htmlToText(guidanceNotes).trim();
  const guidanceNotesTextCharCountDisplay = formatNumber(
    guidanceNotesText.length,
  );
  const guidanceNotesCharLimitDisplay = formatNumber(GUIDANCE_NOTES_CHAR_LIMIT);
  const guidanceNotesFooter = `${guidanceNotesTextCharCountDisplay}/${guidanceNotesCharLimitDisplay}`;
  const guidanceNotesOverLimit =
    guidanceNotesText.length > GUIDANCE_NOTES_CHAR_LIMIT;
  // must come from validation errors because they shouldn't display until submission
  const guidanceNotesMissing = variationForm.validationErrors?.find(
    (message) => message === 'text missing',
  );
  const guidanceNotesAPIErrors = variationForm.errors?.filter(
    (error) => error.source?.pointer === '/guidance',
  );
  const getGuidanceNotesError = () => {
    if (guidanceNotesOverLimit)
      return intl.formatMessage(
        {
          id: 'VariationForm.guidanceNotesOverLimitError',
          defaultMessage:
            '{footer} (Guidance notes cannot be longer than {limit} characters)',
          description:
            'Error message when guidance notes exceed character limit. Example: footer = "Character count: 1200/1000", limit = "1000"',
        },
        {
          footer: guidanceNotesFooter,
          limit: guidanceNotesCharLimitDisplay,
        },
      );
    if (guidanceNotesMissing) return clauseMissingText;
    if (guidanceNotesAPIErrors?.length)
      return guidanceNotesAPIErrors.map((error) => error.detail).join(' ');
    return undefined;
  };

  const clauseRawText = htmlToText(clauseText).trim();
  const clauseRawTextCharCountDisplay = formatNumber(clauseRawText.length);
  const variationTextCharLimitDisplay = formatNumber(VARIATION_TEXT_CHAR_LIMIT);
  const clauseTextFooter = `${clauseRawTextCharCountDisplay}/${variationTextCharLimitDisplay}`;
  const clauseTextOverLimit = clauseRawText.length > VARIATION_TEXT_CHAR_LIMIT;
  // must come from validation errors because they shouldn't display until submission
  const clauseTextMissing = variationForm.validationErrors?.find(
    (message) => message === 'text missing',
  );
  const clauseTextAPIErrors = variationForm.errors?.filter(
    (error) => error.source?.pointer === '/text',
  );
  const getClauseTextError = () => {
    if (clauseTextOverLimit)
      return intl.formatMessage(
        {
          id: 'VariationForm.clauseTextOverLimitError',
          defaultMessage:
            '{footer} (Text cannot be longer than {limit} characters)',
          description:
            'Error message when clause text exceeds character limit. Example: footer = "Character count: 2500/2000", limit = "2000"',
        },
        {
          footer: clauseTextFooter,
          limit: variationTextCharLimitDisplay,
        },
      );
    if (clauseTextMissing) return clauseMissingText;
    if (clauseTextAPIErrors?.length)
      return clauseTextAPIErrors.map((error) => error.detail).join(' ');
    return undefined;
  };

  function handleVariationNameUpdate(name: string) {
    setVariationName(name);
    const errors = variationForm.errors?.filter(
      (error) => error.source?.pointer && error.source.pointer !== '/nickname',
    );
    dispatch(
      slices.clauseLibrary.actions.setVariationForm({
        ...variationForm,
        name,
        errors,
      }),
    );
  }

  // TODO: update type
  function handlePositionUpdate(position: any) {
    setPosition(position);
    const errors = variationForm.errors?.filter(
      (error) => error.source?.pointer && error.source.pointer !== '/position',
    );
    const validationErrors = variationForm.validationErrors?.filter(
      (error) => error !== 'position missing',
    );
    dispatch(
      slices.clauseLibrary.actions.setVariationForm({
        ...variationForm,
        position,
        validationErrors,
        errors,
      }),
    );
  }

  function handleGuidanceNotesUpdate(editorInput: any) {
    const guidanceNotes = editorInput.getData();
    setGuidanceNotes(guidanceNotes);
    dispatch(
      slices.clauseLibrary.actions.setVariationGuidanceNotes(guidanceNotes),
    );
    dispatch(slices.clauseLibrary.actions.resetVariationGuidanceError());
    dispatch(
      slices.clauseLibrary.actions.resetVariationEditorsValidationErrors(),
    );
  }

  function handleClauseTextUpdate(editorInput: any) {
    const clauseText = editorInput.getData();
    setClauseText(clauseText);

    dispatch(slices.clauseLibrary.actions.setVariationClauseText(clauseText));
    dispatch(
      slices.clauseLibrary.actions.resetVariationEditorsValidationErrors(),
    );
    dispatch(slices.clauseLibrary.actions.resetVariationTextError());
  }

  function handleDismissErrorAlert() {
    dispatch(
      slices.clauseLibrary.actions.setVariationForm({
        ...variationForm,
        errors: undefined,
      }),
    );
  }

  const VARIATION_FORM_POINTERS = [
    '/nickname',
    '/guidance',
    '/text',
    '/position',
  ];

  return (
    <Layout direction="column" spacing={8} w="100%">
      {!!variationForm?.errors &&
        variationForm.errors.map((error) => {
          if (
            error.source?.pointer &&
            VARIATION_FORM_POINTERS.includes(error.source.pointer)
          )
            return <></>;
          return (
            <Alert
              key="warning"
              enableIcon
              variant="danger"
              onDismiss={handleDismissErrorAlert}
            >
              <Layout direction="column" spacing={2}>
                <Text variant="body">{error.detail}</Text>
              </Layout>
            </Alert>
          );
        })}
      <Layout w="100%">
        <Layout w="80%" direction="column" pr={4}>
          <FormField
            name="clause-library-variation-name"
            label={intl.formatMessage({
              id: 'VariationForm.variationNameLabel',
              defaultMessage: 'Clause variation Name',
              description: 'Label for variation name field',
            })}
            input={TextInput}
            value={variationName}
            placeholder={variationForm.namePlaceholder}
            // @ts-ignore
            onChange={handleVariationNameUpdate}
            footer={variationNameFooter}
            error={getVariationNameError()}
          />
        </Layout>
        <Layout direction="column" spacing={2}>
          <Label>
            {intl.formatMessage({
              id: 'VariationForm.positionLabel',
              defaultMessage: 'Position',
              description: 'Label for position field',
            })}
            <Text color="status.danger">*</Text>
          </Label>
          <Layout direction="column" spacing={1}>
            <Select
              isMulti={false}
              options={positionOptions}
              onChange={handlePositionUpdate}
              name="position"
              value={position}
              error={getPositionError()}
              enableErrorMessage
            />
          </Layout>
        </Layout>
      </Layout>
      <Layout direction="column" spacing={4} aria-labelledby="guidance-notes">
        <Label id="guidance-notes">
          {intl.formatMessage({
            id: 'VariationForm.guidanceNotesLabel',
            defaultMessage: 'Guidance Notes',
            description: 'Label for guidance notes field',
          })}
        </Label>
        <Layout direction="column" spacing={2}>
          <CKEditor
            data={guidanceNotes}
            config={editorConfig}
            onChange={handleGuidanceNotesUpdate}
          />
          {getGuidanceNotesError() ? (
            <Text color="status.danger" variant="tiny" role="note">
              {getGuidanceNotesError()}
            </Text>
          ) : (
            <Text color="text.secondary" variant="tiny" role="note">
              {guidanceNotesFooter}
            </Text>
          )}
        </Layout>
      </Layout>
      <Layout direction="column" spacing={4} aria-labelledby="clause-text">
        <Label id="clause-text">
          {intl.formatMessage({
            id: 'VariationForm.clauseTextLabel',
            defaultMessage: 'Clause text',
            description: 'Label for clause text field',
          })}
        </Label>
        <Layout direction="column" spacing={2}>
          <CKEditor
            data={clauseText}
            config={editorConfig}
            onChange={handleClauseTextUpdate}
          />
          {getClauseTextError() ? (
            <Text color="status.danger" variant="tiny" role="note">
              {getClauseTextError()}
            </Text>
          ) : (
            <Text color="text.secondary" variant="tiny" role="note">
              {clauseTextFooter}
            </Text>
          )}
        </Layout>
      </Layout>
    </Layout>
  );
};

export default VariationForm;
